<script setup lang="ts">
import { onUnmounted, ref, computed, watchEffect, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDashboards, type OmniFilterChangedPayload } from '@/stores/dashboards'
import { handleError } from '@/lib/error'
import { useColorMode } from '@vueuse/core'
import { usePostHog } from '@/lib/posthog'

const route = useRoute()
const router = useRouter()
const dashboards = useDashboards()
const colorMode = useColorMode()
const { posthog } = usePostHog()

const dashboardId = computed(() => route.params.id as string)

// filters passed in the page URL (e.g. by the Ad library button) are forwarded to Omni
const urlFilters = computed(() => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(route.query)) {
    if ((key.startsWith('f--') || key.startsWith('af--')) && typeof value === 'string') {
      params.append(key, value)
    }
  }
  return params.toString() || null
})

// Get dashboard data from store
const dashboard = computed(() => {
  if (!dashboardId.value) return null
  return dashboards.getDashboard(dashboardId.value)
})

// ponytail: a hidden dashboard titled "<title> · AI" is shown above this one (Omni apps can't render AI summary tiles);
// add a header_dashboard_id column if this spreads beyond a couple of pages
const companion = computed(() => {
  if (!dashboard.value) return null
  const title = `${dashboard.value.title} · AI`
  return dashboards.analysis.flatMap((a) => a.dashboards).find((d) => d.title === title) ?? null
})
const companionIframe = ref<string | null>(null)
const companionFrame = ref<HTMLIFrameElement | null>(null)
const companionHeight = ref(640)

const stopWatchingCompanion = watchEffect(async () => {
  companionIframe.value = null
  if (!companion.value) return
  companionIframe.value = await dashboards.getDashboardIframe(companion.value.id, colorMode.value)
})

const dashboardIframe = ref<string | null>(null)
const dashboardIframeReady = ref(false)
const dashboardIframeStatus = ref<string | null>(null)
const OMNI_STATUS_DONE = 'done'

// Omni apps push iframe history entries (on load and on each filter change) that share the
// browser history with us, so Back first steps "inside" the iframe with no visible effect.
// Such a step shows up as a page:changed back to a URL the iframe already showed, with no app
// activity just before it: we step back once more. (A slow load also sends a late page:changed,
// but always to a new URL.)
// shortcut: Forward into an iframe entry is treated the same way (rare); 0 = app not loaded yet
let appActivityAt = 0
const APP_ACTIVITY_WINDOW_MS = 3000
let iframeHrefs = new Set<string>()
let iframeHref = ''

const stopWatchingDashboardId = watch(dashboardId, () => {
  appActivityAt = 0
  iframeHrefs = new Set()
  iframeHref = ''
  dashboardIframe.value = null
  dashboardIframeReady.value = false
  dashboardIframeStatus.value = null
})

// URL filters are read once per load (not watched): storing the app's filters in the URL must not reload it
const stopWatching = watch(
  // dashboard: re-run once the org's dashboards have loaded
  [dashboardId, colorMode, () => dashboard.value?.id],
  async () => {
    if (!dashboardId.value) return null

    dashboardIframe.value = null
    try {
      dashboardIframe.value = await dashboards.getDashboardIframe(
        dashboardId.value,
        colorMode.value,
        urlFilters.value,
      )
    } catch (error) {
      handleError(error, 'Failed to load dashboard')
    }
  },
  { immediate: true },
)

// filters that follow the user across app pages, by field ({ f: {field: values}, date, dateCustom }), per tab.
// An app reports the fields it has (own) and those changed from its defaults (f): own fields left at their
// default are dropped, other pages' fields are kept.
const SHARED_FILTERS_KEY = 'ginjer-app-filters'
type SharedFilters = { f: Record<string, unknown>; date?: unknown; dateCustom?: unknown }
function readSharedFilters(): SharedFilters | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(SHARED_FILTERS_KEY) ?? 'null')
    return value && typeof value === 'object' && value.f && typeof value.f === 'object'
      ? value
      : null
  } catch {
    return null
  }
}
function saveSharedFilters(update: Record<string, unknown>) {
  if (!update.f || typeof update.f !== 'object' || !Array.isArray(update.own)) return
  const prev = readSharedFilters() ?? { f: {} }
  const f = { ...prev.f }
  for (const field of update.own) if (typeof field === 'string') delete f[field]
  Object.assign(f, update.f)
  const next: SharedFilters = update.ownDate
    ? { f, date: update.date, dateCustom: update.dateCustom }
    : { f, date: prev.date, dateCustom: prev.dateCustom }
  try {
    const json = JSON.stringify(next)
    if (json.length < 20000) sessionStorage.setItem(SHARED_FILTERS_KEY, json)
  } catch {
    // storage unavailable (private mode): filters just don't follow across pages
  }
}

// sandboxed Omni apps can't navigate the top window, so they post
// { source: 'ginjer-app', name: 'navigate', path: '/apps/<id>?af--…' }.
// Origin isn't checked (sandboxed frames may report "null"): only the org's own dashboards are reachable.
function navigateFromApp(path: string) {
  const [pathname, search = ''] = path.split(/\?(.*)/s)
  // `from` powers the Back button: Omni's iframe adds its own history entries
  const query = { ...Object.fromEntries(new URLSearchParams(search)), from: route.fullPath }
  const orgId = route.params.orgId as string | undefined
  const target = dashboards.analysis
    .flatMap((a) => a.dashboards)
    .find((d) => d.dashboard_url === pathname)
  if (!target) return
  router.push(
    orgId
      ? { name: 'admin-preview-dashboard', params: { orgId, id: target.id }, query }
      : { name: 'dashboard', params: { id: target.id }, query },
  )
}

const iframeMessageListener = (event: MessageEvent) => {
  const { source, name, path } = event.data ?? {}
  if (source === 'ginjer-app' && name === 'navigate' && typeof path === 'string') {
    navigateFromApp(path)
    return
  }
  // the app's filters live in our URL (replace: no history entry), so Back and reload restore them
  if (source === 'ginjer-app' && name === 'filters' && event.data.sel) {
    appActivityAt = Date.now()
    if (event.data.shared && typeof event.data.shared === 'object')
      saveSharedFilters(event.data.shared)
    router.replace({ query: { ...route.query, sel: JSON.stringify(event.data.sel) } })
    return
  }
  // the app's search prompt, turned into filters by Omni AI; on failure the app uses its own parser
  if (source === 'ginjer-app' && name === 'ai-filters' && typeof event.data.prompt === 'string') {
    const reply = (payload: object) =>
      (event.source as Window | null)?.postMessage(
        { source: 'ginjer', name: 'ai-filters', id: event.data.id, ...payload },
        '*',
      )
    dashboards
      .getAiFilters(dashboardId.value, event.data.prompt.slice(0, 500))
      .then((filters) => reply({ filters }))
      .catch(() => reply({ error: true }))
    return
  }
  // the app's AI analysis modules: Ginjer AI answers streamed back chunk by chunk, then done (or error)
  if (source === 'ginjer-app' && name === 'ai-stream') {
    const reply = (payload: object) =>
      (event.source as Window | null)?.postMessage(
        { source: 'ginjer', name: 'ai-stream', id: event.data.id, ...payload },
        '*',
      )
    dashboards
      .aiStream(dashboardId.value, event.data.body, (delta) => reply({ delta }))
      .then(() => reply({ done: true }))
      .catch((error: Error) => reply({ error: error.message }))
    return
  }
  // always reply: the app waits for this before its first query run
  if (source === 'ginjer-app' && name === 'hello') {
    appActivityAt = Date.now()
    // sel: the app's own saved filters; af: the filters of a link into this app (e.g. "See ads"),
    // which the app starts from when there is no saved selection (all af-- params carry the same object);
    // shared: the filters set on other pages, used when there is neither
    let sel = null
    let af = null
    try {
      sel = typeof route.query.sel === 'string' ? JSON.parse(route.query.sel) : null
      const link = Object.entries(route.query).find(([k]) => k.startsWith('af--'))?.[1]
      af = typeof link === 'string' ? JSON.parse(link) : null
    } catch {
      // malformed params in a hand-edited URL: the app keeps its defaults
    }
    ;(event.source as Window | null)?.postMessage(
      // origin: the app builds its links into other pages ("Open ad") on this host
      {
        source: 'ginjer',
        name: 'restore',
        sel,
        af,
        shared: readSharedFilters(),
        theme: colorMode.value,
        origin: window.location.origin,
        clientName: dashboards.clientName,
      },
      '*',
    )
    return
  }

  if (!event.origin.includes('omni')) return

  if (event.source === companionFrame.value?.contentWindow) {
    if (event.data.name === 'size' && event.data.payload?.height) {
      // ponytail: capped, the embedded dashboard scrolls internally beyond that
      companionHeight.value = Math.min(event.data.payload.height, 1000)
    }
    return
  }

  if (appActivityAt && event.data.name === 'app:filter-changed') appActivityAt = Date.now()
  const href = event.data.name === 'page:changed' ? event.data.payload?.href : null
  if (typeof href === 'string') {
    const steppedBack =
      appActivityAt &&
      href !== iframeHref &&
      iframeHrefs.has(href) &&
      Date.now() - appActivityAt > APP_ACTIVITY_WINDOW_MS
    iframeHrefs.add(href)
    iframeHref = href
    if (steppedBack) {
      window.history.back()
      return
    }
  }

  // this is to track the initialisation of the iframe;
  // if we don't do that, we get the dashboard:filters event as part of the initialisation
  if (dashboardIframeStatus.value !== OMNI_STATUS_DONE && event.data.name === 'status') {
    dashboardIframeStatus.value = event.data.payload.status
  }

  // ensuring the initial status is done means that each 'dashboard:filter-changed'
  // is triggered by an user action
  if (
    event.data.name === 'dashboard:filter-changed' &&
    dashboardIframeStatus.value === OMNI_STATUS_DONE
  ) {
    // update user filters in local storage
    dashboards.updateUserFilters(event.data.payload as OmniFilterChangedPayload)

    // track filter changes in PostHog
    for (const [filterField, { next }] of Object.entries(
      event.data.payload as OmniFilterChangedPayload,
    )) {
      posthog.capture('Dashboard filter changed', {
        filterField,
        filterLabel: next.filter.label,
        value: decodeURIComponent(next.asJsonUrlSearchParam),
      })
    }
  }
}

onMounted(() => {
  window.addEventListener('message', iframeMessageListener)
})

onUnmounted(() => {
  stopWatching()
  stopWatchingDashboardId()
  stopWatchingCompanion()
  window.removeEventListener('message', iframeMessageListener)
})

// only same-app paths, never protocol-relative URLs
const backPath = computed(() => {
  const from = route.query.from
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : null
})

function onIframeContentLoaded() {
  dashboardIframeReady.value = true
}
</script>

<template>
  <AppComponent :id="dashboardId" :title="dashboard ? dashboard.title : 'Missing dashboard'">
    <template #actions>
      <UButton
        v-if="backPath"
        :to="backPath"
        icon="i-lucide-arrow-left"
        color="neutral"
        variant="ghost"
        label="Back"
      />
    </template>
    <div
      v-if="companionIframe"
      class="mb-4 shrink-0 rounded-lg border border-default bg-default overflow-hidden"
      :style="{ height: `${companionHeight}px` }"
    >
      <iframe ref="companionFrame" :src="companionIframe"></iframe>
    </div>
    <div
      v-if="dashboardIframe"
      :class="{
        'h-full': dashboardIframeReady && !companionIframe,
        'h-[calc(100vh-8rem)] shrink-0': dashboardIframeReady && companionIframe,
        hidden: !dashboardIframeReady,
      }"
    >
      <!-- microphone: voice dictation in the embedded AI analyst chat -->
      <iframe
        :src="dashboardIframe"
        allow="microphone; clipboard-write; fullscreen"
        allowfullscreen
        v-on:load="onIframeContentLoaded"
      ></iframe>
    </div>
    <div
      v-if="!dashboardIframeReady"
      class="flex flex-row gap-2 align-items-end justify-center m-20"
    >
      <USkeleton class="h-[250px] w-4 self-end" />
      <USkeleton class="h-[50px] w-4 self-end" />
      <USkeleton class="h-[200px] w-4 self-end" />
      <USkeleton class="h-[150px] w-4 self-end" />
      <USkeleton class="h-[50px] w-4 self-end" />
      <USkeleton class="h-[200px] w-4 self-end" />
      <USkeleton class="h-[200px] w-4 self-end" />
      <USkeleton class="h-[150px] w-4 self-end" />
      <USkeleton class="h-[50px] w-4 self-end" />
      <USkeleton class="h-[250px] w-4 self-end" />
      <USkeleton class="h-[50px] w-4 self-end" />
      <USkeleton class="h-[200px] w-4 self-end" />
      <USkeleton class="h-[200px] w-4 self-end" />
      <USkeleton class="h-[150px] w-4 self-end" />
    </div>
  </AppComponent>
</template>

<style scoped>
iframe {
  width: 100%;
  height: 100%;
  border: none;
  overflow: hidden;
  display: auto;
  box-sizing: border-box;
}
</style>
